import React, { useRef, useState } from 'react';
import { ArrowUp, ArrowDown, Trash2, Loader2, ImagePlus, Plus, Upload } from 'lucide-react';
import { files as filesApi, newsletterApi } from '../services/api';
import { toast } from '../hooks/use-toast';

export const APP_PAGES = [
  ['/', 'Home'], ['/directory', 'Business directory'], ['/events', 'Events'], ['/auction', 'Auction'],
  ['/community', 'Community'], ['/news', 'News'], ['/donate', 'Donate'], ['/coupons', 'Coupons'],
  ['/how-it-works', 'How it works'], ['/plans', 'Business plans'], ['/blackout', 'Blackout Game'],
  ['/leaderboard', 'Leaderboard'], ['/signup', 'Member sign-up'], ['/newsletter/join', 'Newsletter sign-up'],
];

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

export function useUpload() {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { return await fn(); }
    catch (err) { toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try again.' }); return null; }
    finally { setBusy(false); }
  };
  return [busy, run];
}

const RowTools = ({ i, len, onMove, onRemove, testId }) => (
  <div className="flex gap-1">
    <button type="button" onClick={() => onMove(i, -1)} disabled={i === 0} className="p-1.5 rounded border border-[#2a2a2a] disabled:opacity-30" data-testid={`${testId}-up-${i}`}><ArrowUp size={14} /></button>
    <button type="button" onClick={() => onMove(i, 1)} disabled={i === len - 1} className="p-1.5 rounded border border-[#2a2a2a] disabled:opacity-30" data-testid={`${testId}-down-${i}`}><ArrowDown size={14} /></button>
    <button type="button" onClick={() => onRemove(i)} className="p-1.5 rounded border border-red-500/40 text-red-300" data-testid={`${testId}-remove-${i}`}><Trash2 size={14} /></button>
  </div>
);

export function ImageListEditor({ images, onChange }) {
  const ref = useRef(null);
  const [busy, run] = useUpload();
  const add = async (fileList) => {
    const out = [...images];
    for (const f of Array.from(fileList || [])) {
      const res = await run(() => filesApi.upload(f, 'post'));
      if (res?.url) out.push(res.url);
    }
    onChange(out);
    if (ref.current) ref.current.value = '';
  };
  return (
    <div className="space-y-3" data-testid="nl-content-images">
      {images.map((u, i) => (
        <div key={`${u}-${i}`} className="flex items-start gap-3 bg-black/30 border border-[#1f1f1f] rounded-lg p-2" data-testid={`nl-image-${i}`}>
          <div className="text-xs text-gray-400 w-5 pt-1">{i + 1}</div>
          <img src={filesApi.urlFor(u)} alt="" className="w-28 sm:w-40 rounded object-cover" />
          <div className="ml-auto"><RowTools i={i} len={images.length} testId="nl-image" onMove={(a, b) => onChange(move(images, a, b))} onRemove={(a) => onChange(images.filter((_, k) => k !== a))} /></div>
        </div>
      ))}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden onChange={e => add(e.target.files)} data-testid="nl-image-file" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="nl-add-images"
        className="px-3 py-2 rounded-lg border border-dashed border-green-500/50 text-green-300 text-sm inline-flex items-center gap-2">
        {busy ? <Loader2 className="animate-spin" size={14} /> : <ImagePlus size={14} />} Upload newsletter images
      </button>
      <p className="text-xs text-gray-400">JPG or PNG work in every email app. Images show full width, top to bottom, in this order.</p>
    </div>
  );
}

export function SingleImagePicker({ value, onChange, label, testId, disabled }) {
  const ref = useRef(null);
  const [busy, run] = useUpload();
  const pick = async (f) => {
    const res = await run(() => filesApi.upload(f, 'post'));
    if (res?.url) onChange(res.url);
  };
  return (
    <div className="space-y-2">
      {value && <img src={filesApi.urlFor(value)} alt="" className="w-full max-w-md rounded border border-[#1f1f1f]" data-testid={`${testId}-img`} />}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={e => e.target.files?.[0] && pick(e.target.files[0])} data-testid={`${testId}-file`} />
      {!disabled && (
        <div className="flex gap-2">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId}
            className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center gap-2 text-gray-100">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {value ? `Replace ${label}` : `Upload ${label}`}
          </button>
          {value && <button type="button" onClick={() => onChange(null)} className="text-xs text-red-300" data-testid={`${testId}-clear`}>Remove</button>}
        </div>
      )}
    </div>
  );
}

function ButtonRow({ b, i, len, onPatch, onMove, onRemove }) {
  const fileRef = useRef(null);
  const [busy, run] = useUpload();
  const upFile = async (f) => {
    const res = await run(() => newsletterApi.uploadFile(f));
    if (res?.url) onPatch({ url: res.url, fileName: res.name });
  };
  return (
    <div className="bg-black/30 border border-[#1f1f1f] rounded-lg p-3 space-y-3" data-testid={`nl-button-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-400">Button {i + 1}</span>
        <select value={b.kind} onChange={e => onPatch({ kind: e.target.value })} className="pbb-input !w-auto !py-1 text-sm" data-testid={`nl-button-kind-${i}`}>
          <option value="text">Text button</option>
          <option value="image">Picture button</option>
        </select>
        <label className="inline-flex items-center gap-1 text-sm text-gray-200">
          <input type="checkbox" checked={b.sponsor} onChange={e => onPatch({ sponsor: e.target.checked })} data-testid={`nl-button-sponsor-${i}`} /> Sponsor
        </label>
        <div className="ml-auto"><RowTools i={i} len={len} testId="nl-button" onMove={onMove} onRemove={onRemove} /></div>
      </div>
      <input value={b.label} onChange={e => onPatch({ label: e.target.value })} maxLength={80} className="pbb-input"
        placeholder={b.kind === 'image' ? 'Short description of the picture (for screen readers)' : 'Button label, e.g. Shop the Directory'} data-testid={`nl-button-label-${i}`} />
      {b.kind === 'image' && <SingleImagePicker value={b.image} onChange={(v) => onPatch({ image: v })} label="button picture" testId={`nl-button-image-${i}`} />}
      <div className="grid sm:grid-cols-[180px_1fr] gap-2">
        <select value={b.linkType} onChange={e => onPatch({ linkType: e.target.value, url: e.target.value === 'page' ? '/' : '' })} className="pbb-input" data-testid={`nl-button-linktype-${i}`}>
          <option value="page">Page in the app</option>
          <option value="file">File download</option>
          <option value="web">Web address (campaign, form, survey…)</option>
        </select>
        {b.linkType === 'page' ? (
          <select value={b.url} onChange={e => onPatch({ url: e.target.value })} className="pbb-input" data-testid={`nl-button-page-${i}`}>
            {APP_PAGES.map(([p, l]) => <option key={p} value={p}>{l}</option>)}
          </select>
        ) : b.linkType === 'file' ? (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" hidden onChange={e => e.target.files?.[0] && upFile(e.target.files[0])} data-testid={`nl-button-file-input-${i}`} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center gap-2 text-gray-100" data-testid={`nl-button-file-${i}`}>
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {b.url ? 'Replace file' : 'Upload file'}
            </button>
            <span className="text-xs text-gray-300 truncate">{b.fileName || (b.url ? 'File uploaded' : 'PDF, Word, Excel, image… up to 20 MB')}</span>
          </div>
        ) : (
          <input value={b.url} onChange={e => onPatch({ url: e.target.value })} className="pbb-input" placeholder="https://…" data-testid={`nl-button-url-${i}`} />
        )}
      </div>
    </div>
  );
}

export function ButtonsEditor({ buttons, onChange }) {
  const patch = (i, p) => onChange(buttons.map((b, k) => (k === i ? { ...b, ...p } : b)));
  const add = () => onChange([...buttons, { id: Math.random().toString(36).slice(2, 12), kind: 'text', label: '', image: null, url: '/', linkType: 'page', sponsor: false }]);
  return (
    <div className="space-y-3" data-testid="nl-buttons-editor">
      {buttons.map((b, i) => (
        <ButtonRow key={b.id} b={b} i={i} len={buttons.length} onPatch={(p) => patch(i, p)}
          onMove={(a, dir) => onChange(move(buttons, a, dir))} onRemove={(a) => onChange(buttons.filter((_, k) => k !== a))} />
      ))}
      <button type="button" onClick={add} data-testid="nl-add-button" className="px-3 py-2 rounded-lg border border-dashed border-green-500/50 text-green-300 text-sm inline-flex items-center gap-2">
        <Plus size={14} /> Add a button
      </button>
    </div>
  );
}
